import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Empty } from "antd";
import { ClockIcon } from "@heroicons/react/24/outline";

import {
  apiGetPointPlans,
  apiGetMeWallet,
  apiGetPointSystemConfig,
} from "api/api";
import { gaHandleGaClickEvent } from "components/ga/index";
import { normalizePointPlan } from "utility/options/pointsPlans";

import PointsPlanCard from "./components/PointsPlanCard";
import { useSiteConfig } from "utility/hooks/useSiteConfig";
import { CONFIG_GROUP } from "config/siteContent";

const flowStepIcons = ["🎟️", "🎬", "✉️"];

const PointsPurchase = () => {
  const navigate = useNavigate();
  const config = useSiteConfig(CONFIG_GROUP.POINT);

  const flowSteps = flowStepIcons.map((icon, i) => ({
    step: String(i + 1).padStart(2, "0"),
    icon,
    title: config.get(`howItWorksStep${i + 1}Title`),
    desc: config.get(`howItWorksStep${i + 1}Description`),
  }));

  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [terms, setTerms] = useState([]);

  const isLogin =
    sessionStorage._skyFilm_t && sessionStorage._skyFilm_t.length > 0;

  // # 取得上架中的點數方案（允許匿名）
  useEffect(() => {
    const fetchPlans = async () => {
      setIsLoading(true);
      try {
        const res = await apiGetPointPlans();
        const list = (res.data.content || []).map(normalizePointPlan);
        setPlans(list);
      } catch (err) {
        // 方案列表取不到就顯示空狀態，不擋整頁（後端尚未部署此端點時會是 404）
        console.log("err", err);
        setPlans([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlans();
    // eslint-disable-next-line
  }, []);

  // # 注意事項由後台 SystemConfig 的 Point / Notice 維護；尚未設定時不顯示區塊。
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const res = await apiGetPointSystemConfig();
        const value = res.data?.content?.value;
        setTerms(
          typeof value === "string"
            ? value
                .split("\n")
                .map((term) => term.trim())
                .filter(Boolean)
            : [],
        );
      } catch (err) {
        // 404 表示營運尚未設定 Notice，依 API 約定隱藏區塊即可。
        if (err?.response?.status !== 404) console.log("point notice", err);
        setTerms([]);
      }
    };
    fetchTerms();
  }, []);

  // # 已登入時取得錢包餘額
  useEffect(() => {
    if (!isLogin) return;
    const fetchWallet = async () => {
      try {
        const res = await apiGetMeWallet();
        setWallet(res.data.content);
      } catch (err) {
        console.log("err", err);
      }
    };
    fetchWallet();
  }, [isLogin]);

  const availablePoints = Number(wallet?.totalBalance || 0);
  const frozenPoints = Number(wallet?.totalFrozenBalance || 0);

  // # 立即購買：點數方案不經過購物車，直接建立獨立訂單
  const handleBuy = (plan) => {
    gaHandleGaClickEvent({
      category: "Points",
      action: "選擇點數方案",
      type: plan.name,
    });

    const target = `/checkout?pointId=${plan.id}`;
    if (!isLogin) {
      navigate(`/login?redirect=${encodeURIComponent(target)}`);
      return;
    }
    navigate(target);
  };

  const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-SKF-B-001 text-white">
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-16 pt-20 text-center desktop:pt-28">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[900px] max-w-full -translate-x-1/2"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(76,155,248,0.12) 0%, transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-[720px]">
          <p className="mb-5 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[2px] text-SKF-Blue-001">
            <span className="h-1.5 w-1.5 rounded-full bg-SKF-Blue-001" />
            {config.get("heroLabel")}
          </p>
          <h1 className="mb-4 text-4xl font-bold leading-tight tracking-tight text-white desktop:text-[52px]">
            {config.get("heroTitle")}
          </h1>
          <p className="mx-auto mb-10 max-w-[480px] text-base leading-relaxed text-SKF-G-002 desktop:text-[17px]">
            {config.get("heroSubtitle")}
            <br />
            <span className="text-[15px] text-white/45">
              {config.get("heroDescription")}
            </span>
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => scrollToId("points-packages")}
              className="cursor-pointer rounded-[10px] border-0 bg-white px-6 py-3 text-sm font-bold text-SKF-B-001 transition hover:opacity-85"
            >
              選擇方案
            </button>
            <button
              type="button"
              onClick={() => scrollToId("points-usage")}
              className="cursor-pointer rounded-[10px] border border-solid border-white/20 bg-transparent px-6 py-3 text-sm font-medium text-white transition hover:border-white/50"
            >
              查看使用規則
            </button>
          </div>

          {/* 登入後才顯示目前點數 */}
          {isLogin && wallet && (
            <div className="mt-6 flex justify-center">
              <div className="flex flex-col gap-1.5 rounded-2xl border border-solid border-[rgba(76,155,248,0.25)] bg-[rgba(76,155,248,0.08)] px-5 py-3.5 text-left">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="text-[13px] text-white/50">
                    目前可用點數
                  </span>
                  <span className="text-xl font-bold text-SKF-Blue-001">
                    {availablePoints.toLocaleString()} 點
                  </span>
                </div>
                {frozenPoints > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-SKF-G-002">
                    <ClockIcon className="w-3.5" />
                    凍結中 {frozenPoints.toLocaleString()} 點
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 方案標題 */}
      <div
        id="points-packages"
        className="mx-auto max-w-[1080px] px-4 pb-6 text-left desktop:px-12"
      >
        <p className="mb-2 text-xs font-bold uppercase tracking-[2px] text-white/40">
          Buy Points
        </p>
        <h2 className="mb-2 text-[22px] font-bold text-white">
          {config.get("plansTitle")}
        </h2>
        <p className="text-sm leading-relaxed text-white/45">
          {config.get("plansDescription")}
        </p>
      </div>

      {/* 方案卡片 */}
      <Spin spinning={isLoading}>
        <div className="mx-auto grid max-w-[1080px] grid-cols-1 items-stretch gap-4 px-4 pb-20 tablet:grid-cols-2 laptop:grid-cols-4 desktop:px-12">
          {plans.map((plan) => (
            <PointsPlanCard key={plan.id} plan={plan} onBuy={handleBuy} />
          ))}
        </div>
        {!isLoading && plans.length === 0 && (
          <div className="mx-auto max-w-[1080px] px-4 pb-20 desktop:px-12">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className="rounded-2xl border border-dashed border-white/15 py-16 [&_.ant-empty-description]:text-white/45"
              description="目前沒有上架中的點數方案"
            />
          </div>
        )}
      </Spin>

      {/* 使用流程 */}
      <div
        id="points-usage"
        className="mx-auto mb-16 max-w-[860px] px-4 desktop:px-12"
      >
        <div className="mb-12 text-center">
          <p className="mb-3 text-xs uppercase tracking-[0.15em] text-SKF-Green-001">
            How It Works
          </p>
          <h2 className="m-0 text-[26px] font-bold text-white desktop:text-[38px]">
            {config.get("howItWorksTitle")}
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-8 tablet:grid-cols-3 laptop:grid-cols-3">
          {flowSteps.map((item) => (
            <div
              key={item.step}
              className="rounded-2xl border border-solid border-white/10 bg-white/5 px-7 py-9 text-center"
            >
              <p className="mb-4 text-[11px] font-semibold tracking-[0.12em] text-SKF-Green-001">
                {item.step}
              </p>
              <p className="mb-4 text-4xl">{item.icon}</p>
              <h3 className="mb-2.5 mt-0 text-[17px] font-bold text-white">
                {item.title}
              </h3>
              <p className="m-0 text-sm leading-relaxed text-white/50">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* 注意事項 */}
      {terms.length > 0 && (
        <div className="mx-auto max-w-[640px] px-4 pb-24 desktop:px-12">
          <div className="rounded-2xl border border-solid border-white/10 bg-SKF-G-005 p-6 text-left">
            <p className="mb-3.5 text-sm font-semibold text-white/65">
              注意事項
            </p>
            <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
              {terms.map((term) => (
                <li
                  key={term}
                  className="relative pl-3.5 text-[13px] leading-relaxed text-white/45 before:absolute before:left-0 before:content-['·']"
                >
                  {term}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsPurchase;
